import { useTranslation } from 'react-i18next';
import i18n from '../../../i18n/config';
// arsenal-tpms/frontend/src/components/vehicles/detail/VehicleSensorsTab.jsx
import React from 'react';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    IconButton,
    Tooltip,
    Alert,
    Button,
    useTheme,
    alpha,
    useMediaQuery,
    Divider
} from '@mui/material';
import {
    Delete as DeleteIcon,
    Sensors as SensorIcon,
    SwapHoriz as SwapIcon,
    Add as AddIcon
} from '@mui/icons-material';
import VehicleSchematic from './VehicleSchematic';
import RotationPreview from './RotationPreview';

const ensureString = (value, fallback = '') => {
    if (value === null || value === undefined) return fallback;
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return value.toString();
    return fallback;
};

const VehicleSensorsTab = ({
    currentVehicle,
    editMode,
    rotationMode,
    selectedPositions,
    onUnassignSensor,
    onPositionClick,
    onRegisterSensor,
    onToggleRotationMode,
    onClearSelection,
    unassigning,
    t
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const sensorsWithPositions = currentVehicle.vehicle_sensors || [];

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Control Buttons */}
            <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {editMode && (
                    <Button
                        variant="outlined"
                        startIcon={<AddIcon />}
                        onClick={onRegisterSensor}
                        size={isMobile ? "small" : "medium"}
                    >
                        {i18n.t('sensors.registerSensor')}</Button>
                )}

                {sensorsWithPositions.length >= 2 && (
                    <>
                        {!rotationMode ? (
                            <Button
                                variant="contained"
                                startIcon={<SwapIcon />}
                                onClick={onToggleRotationMode}
                                size={isMobile ? "medium" : "large"}
                            >
                                {t('vehicleDetail.rotation.activateMode', 'Activar Rotación')}
                            </Button>
                        ) : (
                            <>
                                <Button
                                    variant="outlined"
                                    onClick={onToggleRotationMode}
                                    size={isMobile ? "medium" : "large"}
                                >
                                    {t('common.cancel', 'Salir de Rotación')}
                                </Button>
                                {selectedPositions.length > 0 && (
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        onClick={onClearSelection}
                                        size={isMobile ? "medium" : "large"}
                                    >
                                        {t('common.clear', 'Limpiar Selección')}
                                    </Button>
                                )}
                            </>
                        )}
                    </>
                )}
            </Box>

            {/* Rotation Mode Alert */}
            {rotationMode && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                    {t('vehicleDetail.rotation.instructionsActive', 'Toca dos posiciones con sensores para intercambiarlos.')}
                </Alert>
            )}

            {/* Vehicle Schematic in Rotation Mode */}
            {rotationMode && (
                <Box sx={{ mb: 2 }}>
                    <VehicleSchematic
                        vehicle={currentVehicle}
                        t={t}
                        onPositionClick={onPositionClick}
                        rotationMode={rotationMode}
                        selectedPositions={selectedPositions}
                        compact={false}
                    />
                </Box>
            )}

            {/* Rotation Preview */}
            {rotationMode && selectedPositions.length === 2 && (
                <RotationPreview
                    vehicle={currentVehicle}
                    selectedPositions={selectedPositions}
                    t={t}
                />
            )}

            {/* Sensor Table (shown when NOT in rotation mode) */}
            {!rotationMode && (
                <>
                    {sensorsWithPositions.length > 0 ? (
                        <TableContainer
                            component={Paper}
                            variant="outlined"
                            sx={{
                                flexGrow: 1,
                                borderRadius: 2,
                                boxShadow: theme.shadows[2]
                            }}
                        >
                            <Table stickyHeader size={isMobile ? "small" : "medium"}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{
                                            bgcolor: 'primary.main',
                                            color: 'white',
                                            fontWeight: 700,
                                            fontSize: { xs: '0.875rem', sm: '1rem' }
                                        }}>
                                            {t('vehicleDetail.overview.position', 'Posición')}
                                        </TableCell>
                                        <TableCell sx={{
                                            bgcolor: 'primary.main',
                                            color: 'white',
                                            fontWeight: 700,
                                            fontSize: { xs: '0.875rem', sm: '1rem' }
                                        }}>
                                            {t('vehicles.sensorAssignment.sensorId', 'ID del Sensor')}
                                        </TableCell>
                                        {editMode && (
                                            <TableCell sx={{
                                                bgcolor: 'primary.main',
                                                color: 'white',
                                                fontWeight: 700,
                                                fontSize: { xs: '0.875rem', sm: '1rem' }
                                            }}>
                                                {t('common.actions', 'Acciones')}
                                            </TableCell>
                                        )}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {sensorsWithPositions
                                        .sort((a, b) => a.position - b.position)
                                        .map((sensor) => (
                                            <TableRow
                                                key={sensor.id}
                                                sx={{
                                                    '&:hover': {
                                                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                                                    }
                                                }}
                                            >
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Box
                                                            sx={{
                                                                width: { xs: 28, sm: 32 },
                                                                height: { xs: 28, sm: 32 },
                                                                borderRadius: 1,
                                                                bgcolor: 'primary.main',
                                                                color: 'white',
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                fontWeight: 700,
                                                                fontSize: { xs: '0.875rem', sm: '1rem' }
                                                            }}
                                                        >
                                                            {ensureString(sensor.position)}
                                                        </Box>
                                                        {!isMobile && (
                                                            <Typography variant="body2" fontWeight={600}>
                                                                {t('vehicleDetail.overview.position', 'Pos.')} {ensureString(sensor.position)}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <SensorIcon sx={{ fontSize: { xs: 20, sm: 24 }, color: 'primary.main' }} />
                                                        <Typography variant="body2" fontWeight={600} noWrap>
                                                            {ensureString(sensor.sensor_id)}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                {editMode && (
                                                    <TableCell>
                                                        <Tooltip title={t('vehicleDetail.sensors.unassignSensor', 'Desasignar')}>
                                                            <IconButton
                                                                size="small"
                                                                color="error"
                                                                onClick={() => onUnassignSensor(sensor.id)}
                                                                disabled={unassigning}
                                                                sx={{
                                                                    bgcolor: alpha(theme.palette.error.main, 0.1),
                                                                    '&:hover': {
                                                                        bgcolor: alpha(theme.palette.error.main, 0.2),
                                                                    }
                                                                }}
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </TableCell>
                                                )}
                                            </TableRow>
                                        ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : (
                        <Box sx={{
                            textAlign: 'center',
                            py: { xs: 4, sm: 8 },
                            border: 2,
                            borderColor: 'divider',
                            borderRadius: 2,
                            bgcolor: alpha(theme.palette.grey[100], 0.5),
                            flexGrow: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center'
                        }}>
                            <SensorIcon sx={{ fontSize: { xs: 48, sm: 64 }, color: 'text.secondary', mb: 2 }} />
                            <Typography variant={isMobile ? "subtitle1" : "h6"} color="text.secondary" gutterBottom>
                                {t('vehicleDetail.sensors.noSensorsAssigned', 'No hay sensores asignados')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('vehicleDetail.sensors.noSensorsMessage', 'Este vehículo no tiene sensores')}
                            </Typography>
                            {editMode && (
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={onRegisterSensor}
                                    sx={{ mt: 2, mx: 'auto' }}
                                >
                                    {i18n.t('sensors.registerSensor')}</Button>
                            )}
                        </Box>
                    )}

                    {sensorsWithPositions.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                            <Alert severity="info" sx={{ borderRadius: 2 }}>
                                <Typography variant="body2">
                                    {t('vehicleDetail.sensors.sensorsSummary',
                                        t('hardcoded.components_vehicles_detail_vehiclesensorstab.este_vehiculo_tiene_length_sensores', { length: sensorsWithPositions.length, positions_count: currentVehicle.positions_count }),
                                        {
                                            assigned: sensorsWithPositions.length,
                                            total: currentVehicle.positions_count
                                        }
                                    )}
                                </Typography>
                            </Alert>
                        </Box>
                    )}
                </>
            )}
        </Box>
    );
};

export default VehicleSensorsTab;